<i18n>
{
  "en": {
    "engineeringEstimationError": "There was an error generating menu engineering profitability estimation"
  },
  "es": {
    "engineeringEstimationError": "Hubo un error generando la estimación de rentabilidad de menu engineering"
  }
}
</i18n>

<template>
  <div :class="chartClass" v-if="menuProfitability">
    <un-front-chart :type="chartType" :chart-data="chartData" :chart-options="chartOptions"
                    :containerStyles="chartStyles"/>
    <div class="d-flex justify-center" v-if="isEngineeringProfitabilityLoading" :style="spinnerStyles">
      <v-progress-circular indeterminate color="primary" :size="spinnerSize" :width="spinnerWidth"/>
    </div>
    <v-tooltip top v-else-if="menuProfitability.isErrored()">
      <template v-slot:activator="{ on, attrs }">
         <div class="justify-center" v-on="on" v-bind="attrs" :style="spinnerStyles">
          <v-icon color="red" :size="alertSize">mdi-alert</v-icon>
        </div>
      </template>
      <span>{{ errorText }}</span>
    </v-tooltip>
    <div class="font-weight-black d-flex justify-center align-bottom" v-else
         :style="labelStyles">
      {{ profitabilityLabel }}
    </div>

  </div>
</template>

<script>
import Vue from 'vue';
import Component from 'vue-class-component';
import UnFrontChart, { CHART_TYPES } from '@ulibs/unfrontcharts';
import { Prop } from 'vue-property-decorator';
import { Components, MapGetters } from '@/apps/core/helpers/decorators';

export const SIZE_TYPES = {
  SMALL: 's',
  MEDIUM: 'md'
};

@Component
@Components({ UnFrontChart })
@MapGetters(['getMenuProfitability', 'isEngineeringProfitabilityLoading'])
export default class EngineeringProfitabilityChart extends Vue {
  @Prop({
    type: String,
    default: SIZE_TYPES.MEDIUM,
    validator: (type) => Object.values(SIZE_TYPES).includes(type)
  }) size;

  @Prop(String) menuGuid;

  @Prop({ type: String, default: 'd-flex align-center justify-center price-chart' }) chartClass;

  get menuProfitability() {
    const menuEngineeringProfitability = this.getMenuProfitability(this.menuGuid);
    return menuEngineeringProfitability.profitability;
  }

  get chartType() {
    return CHART_TYPES.HALF_DOUGHNUT;
  }

  get errorText() {
    return this.$t('engineeringEstimationError');
  }

  get profitabilityLabel() {
    if (!this.menuProfitability.isErrored()) {
      return `${(this.menuProfitability.profitability * 100).toFixed(1)}${this.menuProfitability.units}`;
    }
    return '?';
  }

  get chartData() {
    if (this.menuProfitability.isErrored()) {
      return {
        datasets: [{
          backgroundColor: ['#efefef'],
          data: [1],
          borderWidth: 0,
        }]
      };
    }

    const rest = 0.5 - this.menuProfitability.max_profitability;
    const min = this.menuProfitability.min_profitability;
    const profitability = this.menuProfitability.profitability - this.menuProfitability.min_profitability;
    const max = this.menuProfitability.max_profitability - this.menuProfitability.profitability;
    const negative = this.menuProfitability.isNegative();
    const negativeValue = Math.abs(this.menuProfitability.profitability);
    return {
      datasets: [
        {
          backgroundColor: negative ? ['red', 'red', 'red', '#efefef'] : ['#5de700', '#ff865e', '#00a8ff', '#efefef'],
          data: negative ? [negativeValue, 0, 0, 0.5 - negativeValue] : [min, profitability, max, rest],
          borderWidth: 0,
        }
      ]
    };
  }

  get chartOptions() {
    return {
      cutoutPercentage: 75,
      maintainAspectRatio: true,
      backgroundColor: '#fff',
      legend: {
        display: false
      },
      tooltips: {
        enabled: false
      },
      animation: {
        duration: 0
      }
    };
  }

  get alertSize() {
    switch (this.size) {
      case SIZE_TYPES.SMALL: return '20px';
      case SIZE_TYPES.MEDIUM: return '50px';
      default: return '12px';
    }
  }

  get chartStyles() {
    switch (this.size) {
      case SIZE_TYPES.SMALL: return this.chartStylesSmall;
      case SIZE_TYPES.MEDIUM: return this.chartStylesMedium;
      default: return this.chartStylesMedium;
    }
  }

  get labelStyles() {
    switch (this.size) {
      case SIZE_TYPES.SMALL: return this.labelStylesSmall;
      case SIZE_TYPES.MEDIUM: return this.labelStylesMedium;
      default: return this.labelStylesMedium;
    }
  }

  get spinnerStyles() {
    switch (this.size) {
      case SIZE_TYPES.SMALL: return this.spinnerStylesSmall;
      case SIZE_TYPES.MEDIUM: return this.spinnerStylesMedium;
      default: return this.spinnerStylesMedium;
    }
  }

  get spinnerSize() {
    switch (this.size) {
      case SIZE_TYPES.MEDIUM: return 30;
      case SIZE_TYPES.SMALL: return 15;
      default: return 30;
    }
  }

  get spinnerWidth() {
    switch (this.size) {
      case SIZE_TYPES.MEDIUM: return 3;
      case SIZE_TYPES.SMALL: return 2;
      default: return 3;
    }
  }

  get chartStylesSmall() {
    let chartStyles = {
      height: '50px',
      width: '55px',
    };
    if (this.$vuetify.breakpoint.smAndDown) {
      chartStyles = {
        height: '40px',
        width: '45px',
        top: '-4px'
      };
    }
    return chartStyles;
  }

  get chartStylesMedium() {
    let chartStyles = {
      height: '210px',
      width: '210px'
    };
    if (this.$vuetify.breakpoint.smAndDown) {
      chartStyles = {
        ...chartStyles,
        height: '160px',
        width: '160px'
      };
    }
    return chartStyles;
  }

  get spinnerStylesSmall() {
    let spinnerStyles = {
      color: '#032C58',
      position: 'absolute',
      top: '25px'
    };
    if (this.$vuetify.breakpoint.smAndDown) {
      spinnerStyles = {
        ...spinnerStyles,
        top: '18px'
      };
    }
    return spinnerStyles;
  }

  get spinnerStylesMedium() {
    let spinnerStyles = {
      color: '#032C58',
      position: 'absolute',
      top: '120px',
      fontSize: '24px',
    };
    if (this.$vuetify.breakpoint.smAndDown) {
      spinnerStyles = {
        ...spinnerStyles,
        top: '86px'
      };
    }
    return spinnerStyles;
  }

  get labelStylesSmall() {
    let labelStyles = {
      color: '#032C58',
      position: 'absolute',
      top: '31px',
      'font-size': '12px'
    };
    if (this.$vuetify.breakpoint.smAndDown) {
      labelStyles = {
        ...labelStyles,
        position: 'absolute',
        top: '24px',
        'font-size': '10px'
      };
    }
    return labelStyles;
  }

  get labelStylesMedium() {
    let labelStyles = {
      color: '#032C58',
      position: 'absolute',
      top: '130px',
      fontSize: '24px',
    };
    if (this.$vuetify.breakpoint.smAndDown) {
      labelStyles = {
        ...labelStyles,
        top: '92px'
      };
    }
    return labelStyles;
  }
}
</script>

<style lang="css" scoped>
.price-chart {
  position: relative;
}
</style>
